import { FC, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ILocalDate } from "../../../../entities/calendar";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { useMyActions } from "../../../../entities/my";
import { IShiftCalendarDay, shiftService, TShiftStatus } from "../../../../entities/shift";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { MyError } from "../../../../shared/lib/error/MyError";
import { useGetMonthWeeks } from "../../../../shared/lib/hooks/useGetMonthWeeks";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { WorkTgFilter } from "../../../workTgFilter";
import classes from './shiftCalendarMonth.module.scss'

interface IProps {
    onSelectDate: (date: Date) => void;
}

const getLocalDateFromDate = (date: Date): ILocalDate => ({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
})

const getStatusLabel = (status: TShiftStatus): string => {
    switch(status){
        case 'SHIFT_STATUS_PLANNED':
            return 'Запланировано'
        case 'SHIFT_STATUS_COMPLETED':
            return 'Завершено'
        case 'SHIFT_STATUS_CANCELLED':
            return 'Отменено'
        default:
            return 'Нет смены'
    }
}

export const ShiftCalendarMonth: FC<IProps> = ({onSelectDate}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const [params, setParams] = useSearchParams()
    const selectedWorkTgIds = useMemo(
        () => params
            .getAll('work_tg_ids')
            .map(id => parseInt(id, 10))
            .filter(id => !isNaN(id) && id > 0),
        [params]
    )
    const selectedWorkTgIdsKey = selectedWorkTgIds.join(',')

    const [visibleMonth, setVisibleMonth] = useState<Date>(new Date())
    const [calendarDays, setCalendarDays] = useState<IShiftCalendarDay[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const weeks = useGetMonthWeeks(visibleMonth)
    const monthTitle = visibleMonth
        .toLocaleString('ru-RU', {month: 'long', year: 'numeric'})
        .replace(/\s*г\.$/, '')

    const calendarDaysMap = useMemo(() => {
        return new Map(
            calendarDays.map(day => [
                `${day.date.year}-${day.date.month}-${day.date.day}`,
                day,
            ])
        )
    }, [calendarDays])

    const getCalendarDayInfo = (date: Date) => {
        return calendarDaysMap.get(`${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`)
    }

    const getDayClassName = (date: Date): string => {
        const calendarDay = getCalendarDayInfo(date)

        if(!calendarDay) return ''

        const classesList: string[] = []

        if(calendarDay.assignedTeamCount > 0) classesList.push(classes.hasInfo)
        if(calendarDay.hasReplacements) classesList.push(classes.hasReplacements)

        switch(calendarDay.status){
            case 'SHIFT_STATUS_PLANNED':
                classesList.push(classes.planned)
                break
            case 'SHIFT_STATUS_COMPLETED':
                classesList.push(classes.completed)
                break
            case 'SHIFT_STATUS_CANCELLED':
                classesList.push(classes.cancelled)
                break
        }

        return classesList.join(' ')
    }

    const getListInfo = (date: Date) => {
        const calendarDay = getCalendarDayInfo(date)

        if(!calendarDay || (!calendarDay.assignedTeamCount && !calendarDay.hasReplacements)) return null

        return (
            <ul className={classes.infoList}>
                <li className={classes.item}>
                    <span className={classes.infoLabel}>Статус</span>
                    <span className={classes.infoCount}>{getStatusLabel(calendarDay.status)}</span>
                </li>
                <li className={classes.item}>
                    <span className={classes.infoLabel}>Звеньев назначено</span>
                    <span className={classes.infoCount}>{calendarDay.assignedTeamCount}</span>
                </li>
                {calendarDay.hasReplacements && (
                    <li className={`${classes.item} ${classes.replacementInfo}`}>
                        <span className={classes.infoLabel}>Есть подмены</span>
                        <span className={classes.infoCount}>да</span>
                    </li>
                )}
            </ul>
        )
    }

    useEffect(() => {
        let isActual = true
        const firstDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1)
        const lastDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0)

        setIsLoading(true)
        setCalendarDays([])

        const getData = async() => {
            try{
                const response = await shiftService.getShiftCalendar({
                    from: getLocalDateFromDate(firstDay),
                    to: getLocalDateFromDate(lastDay),
                    tgAccountIds: selectedWorkTgIds.map(String),
                })

                if(isActual) setCalendarDays(response.days)
            }
            catch(e){
                console.log(e)
                if(e instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else if(e instanceof MyError){
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении календаря смен', type: 'error'})
                }
            }
            finally{
                if(isActual) setIsLoading(false)
            }
        }

        getData()

        return () => {
            isActual = false
        }
    }, [visibleMonth, selectedWorkTgIdsKey])

    const onWorkTgIdsChange = (nextWorkTgIds: number[]) => {
        const nextParams = new URLSearchParams(params)
        nextParams.delete('work_tg_ids')

        nextWorkTgIds.forEach(id => {
            if(id > 0) nextParams.append('work_tg_ids', String(id))
        })

        setParams(nextParams)
    }

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <section>
                    <h2 className={classes.title}>{monthTitle}</h2>
                </section>
                <nav className={classes.navigation} aria-label="Переключение месяца">
                    <button
                        type="button"
                        className={classes.navigationButton}
                        onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1))}
                        aria-label="Предыдущий месяц"
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        className={classes.navigationButton}
                        onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1))}
                        aria-label="Следующий месяц"
                    >
                        ›
                    </button>
                </nav>
            </section>

            <section className={classes.filters}>
                <WorkTgFilter
                    label="Рабочие ТГ:"
                    initWorkTgIds={selectedWorkTgIds}
                    setWorkTgIds={onWorkTgIdsChange}
                />
            </section>

            <section className={classes.calendarFrame}>
                {isLoading ? (
                    <section className={classes.loader}>
                        <LoaderSpinner />
                    </section>
                ) : (
                    <table className={classes.month}>
                        <thead>
                            <tr>
                                <th scope="col">Пн</th>
                                <th scope="col">Вт</th>
                                <th scope="col">Ср</th>
                                <th scope="col">Чт</th>
                                <th scope="col">Пт</th>
                                <th scope="col">Сб</th>
                                <th scope="col">Вс</th>
                            </tr>
                        </thead>
                        <tbody>
                            {weeks.map((week, weekIndex) => (
                                <tr key={weekIndex}>
                                    {week.map(day => (
                                        <td
                                            key={day.date.getTime()}
                                            tabIndex={day.class === 'opacity' ? -1 : 0}
                                            onClick={day.class === 'opacity' ? undefined : () => onSelectDate(day.date)}
                                            onKeyDown={event => {
                                                if(day.class === 'opacity') return
                                                if(event.key === 'Enter' || event.key === ' '){
                                                    event.preventDefault()
                                                    onSelectDate(day.date)
                                                }
                                            }}
                                            className={classes.day +
                                                (day.class === 'current' ? ` ${classes.current}` : '') +
                                                (day.class === 'opacity' ? ` ${classes.opacity}` : '') +
                                                (day.class !== 'opacity' ? ` ${getDayClassName(day.date)}` : '')
                                            }
                                        >
                                            {day.class !== 'opacity' && (
                                                <>
                                                    <span className={classes.dayNumber}>{day.date.getDate()}</span>
                                                    {getCalendarDayInfo(day.date)?.assignedTeamCount ? (
                                                        <span className={classes.dayCaption}>
                                                            {getCalendarDayInfo(day.date)?.assignedTeamCount} зв.
                                                        </span>
                                                    ) : null}
                                                    {getCalendarDayInfo(day.date)?.hasReplacements && (
                                                        <span
                                                            className={classes.replacementBadge}
                                                            aria-label="Подмена"
                                                            title="Подмена"
                                                        >
                                                            Подмена
                                                        </span>
                                                    )}
                                                    {getListInfo(day.date)}
                                                </>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </section>
    )
}
